// Client Portal — Distribution calendar/queue view (Buffer-parity item 2).
// Week/month calendar of queued, scheduled, and published posts across
// platforms, with cancel/reschedule on anything the broker still considers
// mutable (queued/scheduled/failed — rescheduling a failed post is the
// documented human-verified retry path).
// ⚠️ All user-facing copy in this file is draft — flagged for human review
// (Hard Rule 8) before client exposure.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
  AlertTriangle,
} from "lucide-react";
import { distributionFetch } from "../lib/api";
import { loadBestTimes, slotLabel } from "../lib/bestTimes";

export const PLATFORM_LABELS = {
  facebook: { label: "Facebook", icon: "📘" },
  instagram: { label: "Instagram", icon: "📸" },
  youtube: { label: "YouTube", icon: "▶️" },
  tiktok: { label: "TikTok", icon: "🎵" },
  linkedin: { label: "LinkedIn", icon: "💼" },
  x: { label: "X (Twitter)", icon: "𝕏" },
};

const POST_STATUS_STYLES = {
  pending_approval: "bg-orange-50 text-orange-700 border-orange-300",
  queued: "bg-sky-50 text-sky-700 border-sky-200",
  scheduled: "bg-indigo-50 text-indigo-700 border-indigo-200",
  publishing: "bg-amber-50 text-amber-700 border-amber-200",
  published: "bg-green-50 text-green-700 border-green-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-gray-50 text-gray-400 border-gray-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

const STATUS_LABELS = { pending_approval: "pending approval" };

const MUTABLE = ["queued", "scheduled", "failed", "pending_approval"];

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (d) => {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
};
const startOfWeek = (d) => {
  const day = startOfDay(d);
  return new Date(day.getTime() - day.getDay() * DAY_MS);
};
const dateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const timeLabel = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

// datetime-local inputs speak local wall-clock time, not ISO/UTC.
const toLocalInputValue = (iso) => {
  const d = iso ? new Date(iso) : new Date(Date.now() + 60 * 60 * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Visible range: month view pads to whole weeks so leading/trailing days show.
const visibleRange = (cursor, view) => {
  if (view === "week") {
    const from = startOfWeek(cursor);
    return { from, to: new Date(from.getTime() + 7 * DAY_MS) };
  }
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const from = startOfWeek(first);
  const lastWeekStart = startOfWeek(
    new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0),
  );
  return { from, to: new Date(lastWeekStart.getTime() + 7 * DAY_MS) };
};

function PostChip({ post, onClick }) {
  const meta = PLATFORM_LABELS[post.platform] || {
    icon: "",
    label: post.platform,
  };
  return (
    <button
      onClick={onClick}
      title={`${meta.label} — ${STATUS_LABELS[post.status] || post.status}${post.title ? ` — ${post.title}` : ""}`}
      className={`w-full text-left text-xs border rounded px-1.5 py-0.5 truncate hover:opacity-75 ${
        POST_STATUS_STYLES[post.status] || "bg-gray-50 border-gray-200"
      } ${post.status === "cancelled" ? "line-through" : ""}`}
    >
      <span className="mr-1">{meta.icon}</span>
      <span className="text-[10px] text-gray-500 mr-1">
        {timeLabel(post.display_at)}
      </span>
      {post.title || post.body_preview || post.content_id}
    </button>
  );
}

function PostDetail({ post, facilityId, onClose, onChanged }) {
  const meta = PLATFORM_LABELS[post.platform] || { label: post.platform };
  const mutable = MUTABLE.includes(post.status);
  const [when, setWhen] = useState(toLocalInputValue(post.schedule_at));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const act = async (path, body) => {
    setBusy(true);
    setError(null);
    try {
      await distributionFetch(`/posts/${facilityId}/${post.post_id}/${path}`, {
        method: "POST",
        ...(body !== undefined && { body: JSON.stringify(body) }),
      });
      await onChanged();
      onClose();
    } catch (e) {
      if (
        e.detail?.status === "post_not_mutable" ||
        e.detail?.status === "post_not_pending"
      ) {
        setError(
          `This post is now ${e.detail.post_status} and can no longer be changed.`,
        );
      } else if (e.detail?.status === "approver_access_required") {
        setError(
          "Approving or rejecting posts needs approver access for this facility.",
        );
      } else {
        setError("The change didn't go through. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleReject = () => {
    const note = window.prompt(
      "Reject this post? Optionally add a note for the requester:",
      "",
    );
    if (note !== null) act("reject", { note: note || null });
  };

  const handleCancel = () => {
    if (
      window.confirm(
        "Cancel this post? It will not be published. This can't be undone from the calendar.",
      )
    )
      act("cancel");
  };

  return (
    <div
      className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="font-semibold">
              {meta.icon} {meta.label}
              <span
                className={`ml-2 text-xs px-2 py-0.5 rounded border ${POST_STATUS_STYLES[post.status] || ""}`}
              >
                {STATUS_LABELS[post.status] || post.status}
              </span>
            </div>
            {post.title && (
              <div className="text-sm font-medium mt-1">{post.title}</div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {post.body_preview && (
          <p className="text-sm text-gray-600 whitespace-pre-wrap border rounded-lg p-3 bg-gray-50">
            {post.body_preview}
          </p>
        )}

        <div className="text-xs text-gray-500 space-y-1">
          {post.published_at ? (
            <div>Published {new Date(post.published_at).toLocaleString()}</div>
          ) : post.schedule_at ? (
            <div>
              Scheduled for {new Date(post.schedule_at).toLocaleString()}
            </div>
          ) : (
            <div>Queued — publishes on the next dispatch run</div>
          )}
          <div>Created {new Date(post.created_at).toLocaleString()}</div>
          {post.external_post_id && (
            <div>Platform ID {post.external_post_id}</div>
          )}
        </div>

        {post.status === "failed" && post.error && (
          <div className="flex gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{post.error}</span>
          </div>
        )}

        {post.status === "rejected" && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
            Rejected
            {post.reviewed_at
              ? ` ${new Date(post.reviewed_at).toLocaleString()}`
              : ""}
            {post.review_note ? ` — “${post.review_note}”` : ""}
          </div>
        )}

        {post.status === "pending_approval" && (
          <div className="border-t pt-4 flex items-center gap-2">
            <button
              onClick={() => act("approve")}
              disabled={busy}
              className="text-sm px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            >
              Approve
            </button>
            <button
              onClick={handleReject}
              disabled={busy}
              className="text-sm px-3 py-1.5 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
            >
              Reject…
            </button>
            <span className="text-xs text-gray-400">
              Approving queues the post for its scheduled time.
            </span>
          </div>
        )}

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            {error}
          </div>
        )}

        {mutable && (
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="border rounded-lg px-2 py-1.5 text-sm"
              />
              <button
                onClick={() =>
                  act("reschedule", {
                    schedule_at: new Date(when).toISOString(),
                  })
                }
                disabled={busy || !when}
                className="text-sm px-3 py-1.5 rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
              >
                {post.status === "failed" ? "Retry at this time" : "Reschedule"}
              </button>
              {post.status === "failed" && (
                <button
                  onClick={() => act("reschedule", { schedule_at: null })}
                  disabled={busy}
                  className="text-sm px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-50"
                >
                  Retry now
                </button>
              )}
            </div>
            <button
              onClick={handleCancel}
              disabled={busy}
              className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
            >
              Cancel this post
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function DistributionCalendar({ facility }) {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => startOfDay(new Date()));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [bestTimes, setBestTimes] = useState(null);

  const { from, to } = useMemo(
    () => visibleRange(cursor, view),
    [cursor, view],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await distributionFetch(
        `/posts/${facility.facility_id}?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
      );
      setPosts(res.items || []);
    } catch {
      setError("Could not load posts. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [facility.facility_id, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Suggestions are an enhancement — a failed fetch just hides the strip.
    loadBestTimes(facility.facility_id)
      .then(setBestTimes)
      .catch(() => setBestTimes(null));
  }, [facility.facility_id]);

  const byDay = useMemo(() => {
    const map = {};
    for (const p of posts) {
      const key = dateKey(new Date(p.display_at));
      (map[key] = map[key] || []).push(p);
    }
    return map;
  }, [posts]);

  const days = useMemo(() => {
    const out = [];
    for (let t = from.getTime(); t < to.getTime(); t += DAY_MS) {
      out.push(new Date(t));
    }
    return out;
  }, [from, to]);

  const step = (dir) => {
    if (view === "week") {
      setCursor(new Date(cursor.getTime() + dir * 7 * DAY_MS));
    } else {
      setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1));
    }
  };

  const todayKey = dateKey(new Date());
  const maxChips = view === "week" ? 12 : 3;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <button
            onClick={() => step(-1)}
            className="p-1.5 rounded hover:bg-gray-100"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => step(1)}
            className="p-1.5 rounded hover:bg-gray-100"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCursor(startOfDay(new Date()))}
            className="text-sm px-3 py-1 rounded border hover:bg-gray-50"
          >
            Today
          </button>
        </div>
        <div className="font-medium">
          {view === "week"
            ? `Week of ${from.toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })}`
            : cursor.toLocaleDateString([], { month: "long", year: "numeric" })}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {loading && (
            <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
          )}
          <div className="flex rounded-lg border overflow-hidden text-sm">
            {["week", "month"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1 capitalize ${view === v ? "bg-sky-600 text-white" : "hover:bg-gray-50"}`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
          {error}
        </div>
      )}

      <div className="border rounded-xl overflow-hidden bg-white">
        <div className="grid grid-cols-7 text-xs text-gray-500 border-b bg-gray-50">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="px-2 py-1.5">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day) => {
            const key = dateKey(day);
            const dayPosts = byDay[key] || [];
            const overflow = dayPosts.length - maxChips;
            const inMonth =
              view === "week" || day.getMonth() === cursor.getMonth();
            return (
              <div
                key={key}
                className={`border-b border-r p-1.5 space-y-1 ${
                  view === "week" ? "min-h-[16rem]" : "min-h-[6.5rem]"
                } ${inMonth ? "" : "bg-gray-50"}`}
              >
                <div
                  className={`text-xs ${
                    key === todayKey
                      ? "font-bold text-sky-600"
                      : inMonth
                        ? "text-gray-600"
                        : "text-gray-400"
                  }`}
                >
                  {day.getDate()}
                </div>
                {dayPosts.slice(0, maxChips).map((p) => (
                  <PostChip
                    key={p.post_id}
                    post={p}
                    onClick={() => setSelected(p)}
                  />
                ))}
                {overflow > 0 && (
                  <button
                    onClick={() => {
                      setCursor(startOfDay(day));
                      setView("week");
                    }}
                    className="text-[10px] text-gray-500 hover:text-sky-600"
                  >
                    +{overflow} more
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
        {Object.entries(POST_STATUS_STYLES).map(([status, cls]) => (
          <span key={status} className={`px-2 py-0.5 rounded border ${cls}`}>
            {STATUS_LABELS[status] || status}
          </span>
        ))}
      </div>

      {bestTimes && (
        <div className="border rounded-xl p-4 bg-white">
          <div className="text-sm font-medium text-gray-700 mb-2">
            Best times to post
            <span className="ml-2 text-xs font-normal text-gray-400">
              from this facility's engagement over the last{" "}
              {bestTimes.window_days} days
            </span>
          </div>
          {Object.entries(bestTimes.platforms || {}).some(
            ([, v]) => v.sufficient,
          ) ? (
            <div className="space-y-1.5 text-sm">
              {Object.entries(bestTimes.platforms)
                .filter(([, v]) => v.sufficient)
                .map(([p, v]) => (
                  <div key={p} className="flex flex-wrap items-center gap-1.5">
                    <span className="w-28 text-gray-600">
                      {PLATFORM_LABELS[p]?.icon}{" "}
                      {PLATFORM_LABELS[p]?.label || p}
                    </span>
                    {v.suggestions.map((slot) => (
                      <span
                        key={`${slot.day_of_week}-${slot.hour}`}
                        title={`avg engagement ${slot.avg_engagement} across ${slot.posts} posts`}
                        className="text-xs px-2 py-0.5 rounded-full border border-gray-200 text-gray-600"
                      >
                        {slotLabel(slot)}
                      </span>
                    ))}
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">
              Not enough posting history yet — suggestions appear once a
              platform has {bestTimes.min_posts}+ published posts with
              engagement data.
            </p>
          )}
        </div>
      )}

      {selected && (
        <PostDetail
          post={selected}
          facilityId={facility.facility_id}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
